import TKFormValidator from '@tk/controls/tk.form.validator';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';

enum TCSpareDialogActions {
    FORWARD = 'forward',
    BACK = 'back',
    SUBMIT = 'submit',
    NEW_CONTACT = 'new-contact',
    SELECT_CONTACT = 'select-contact',
}

export default class TCSpareDialog extends TKCustomElementFactory {
    steps: Element[];
    activeStep: HTMLElement;
    validator?: TKFormValidator;
    contactModeInput?: HTMLInputElement;
    showClassName = 'show-b';
    hideClassName = 'hide';
    actionAttribute = 'data-tc-action';
    stepAttribute = 'data-tc-step';
    buttonSelector = `[${this.actionAttribute}]`;
    contactSelectSelector = '[data-tc-contact-select]';
    contactCreateSelector = '[data-tc-contact-create]';

    constructor() {
        super();
        this.steps = [...this.querySelectorAll(`[${this.stepAttribute}]`)];
        this.activeStep = this.steps[0] as HTMLElement;
        this.contactModeInput = this.querySelector<HTMLInputElement>('[data-tc-contact-mode]') || undefined;
    }

    connectedCallback(): void {
        if (!this.steps.length) return;
        if (!this.contactModeInput) throw new Error('Missing contact mode input.')
        this.registerListener();
    }

    setInitalState() {
        const mode = this.contactModeInput!.value;
        mode === 'select' ? this.displayContactSelect() : this.displayContactInput();
    }

    registerListener(): void {
        const action = this.handleClick.bind(this);
        const event = 'click';

        this.pushListener({
            event,
            element: this,
            action,
        });
    }

    handleClick(event: Event) {
        const target = event.target as HTMLElement;
        if (target.closest(this.buttonSelector)) {
            event.preventDefault();
            const button = target.closest(this.buttonSelector) as HTMLButtonElement;
            const action = button!.getAttribute(this.actionAttribute);

            switch (action) {
                case TCSpareDialogActions.FORWARD:
                    this.setNextStepActive();
                    break;
                case TCSpareDialogActions.BACK:
                    this.setLastStepActive();
                    break;
                case TCSpareDialogActions.NEW_CONTACT:
                    this.displayContactInput();
                    break;
                case TCSpareDialogActions.SELECT_CONTACT:
                    this.displayContactSelect();
                    break;
                case TCSpareDialogActions.SUBMIT:
                    this.submitForm();
                    break;
            }
        }
    }

    async setNextStepActive() {
        this.setInitalState();
        this.validator = this.querySelector('tk-form-validator') ||undefined;
        const nextStepIndex = this.steps.indexOf(this.activeStep) + 1;
        const originalValidationList = this.validator!.validationList;
        const filteredValidationList = originalValidationList.filter(validationItem =>
            validationItem.element.closest(`[${this.stepAttribute}='${nextStepIndex}']`)
        );
        this.validator!.validationList = filteredValidationList;
        const isFormValid = await this.validator!.isFormValid();
        this.validator!.validationList = originalValidationList;
        if (!isFormValid) return;

        const activeStepIndex = this.steps.indexOf(this.activeStep);
        this.activeStep.classList.remove(this.showClassName);
        this.activeStep = this.steps[activeStepIndex + 1] as HTMLElement;
        this.activeStep.classList.add(this.showClassName);
    }

    setLastStepActive() {
        const activeIndex = this.steps.indexOf(this.activeStep);
        this.activeStep.classList.remove(this.showClassName);
        this.activeStep = this.steps[activeIndex - 1] as HTMLElement;
        this.activeStep.classList.add(this.showClassName);
    }

    displayContactInput() {
        const contactCreate = this.querySelector(this.contactCreateSelector) as HTMLElement;
        const contactSelect = this.querySelector(this.contactSelectSelector) as HTMLElement;

        if (contactCreate) {
            contactCreate.classList.remove(this.hideClassName);
            this.toggleRequired(contactCreate, true);
        }
        if (contactSelect) {
            contactSelect.classList.add(this.hideClassName);
            this.toggleRequired(contactSelect, false);
        }

        this.setContactMode('create');
    }

    displayContactSelect() {
        const contactCreate = this.querySelector(this.contactCreateSelector) as HTMLElement;
        const contactSelect = this.querySelector(this.contactSelectSelector) as HTMLElement;

        if (contactCreate) {
            contactCreate.classList.add(this.hideClassName);
            this.toggleRequired(contactCreate, false);
        }
        if (contactSelect) {
            contactSelect.classList.remove(this.hideClassName);
            this.toggleRequired(contactSelect, true);
        }

        this.setContactMode('select');
    }

    setContactMode(mode: 'select' | 'create') {
        this.contactModeInput!.value = mode;

    }

    toggleRequired(container: HTMLElement, enabled: boolean) {
        const fields = container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea');

        fields.forEach(field => {
            if (enabled) {
                field.setAttribute('required', '');
            } else {
                field.removeAttribute('required');
            }
        });
    }

    async submitForm() {
        const originalValidationList = this.validator!.validationList;
        const contactMode = this.contactModeInput!.value;
        const contactFormSelector= contactMode === 'select' ? this.contactSelectSelector : this.contactCreateSelector;
        const filteredValidationList = originalValidationList.filter(validationItem =>
            validationItem.element.closest(`[${this.stepAttribute}='1']`)
            || validationItem.element.closest(contactFormSelector)
        );
        this.validator!.validationList = filteredValidationList;
        const isFormValid = await this.validator!.isFormValid();
        this.validator!.validationList = originalValidationList;
        if (!isFormValid) return;

        const form = this.querySelector('form');
        form && form.submit();
    }
}
